import React, { useMemo } from 'react';
import TrafficLight from '../../../features/diagnostic/components/TrafficLight';
import LeverSlider from '../../../features/diagnostic/components/LeverSlider';
import { simulateLevers } from '../../../features/diagnostic/engine';
import { LEVER_MAP } from '../../../features/diagnostic/engine/levers';
import {
  formatMoney,
  formatMoneyShort,
  formatNumber,
  formatPercent,
  formatSignedPercent,
} from '../../../features/diagnostic/format';

const StepResult = ({ result, state, setSimulation, resetSimulation, onDownload, onUnlock }) => {
  const simulation = useMemo(
    () => simulateLevers(result.state, state.simulation),
    [result.state, state.simulation],
  );

  const { primaryLever, ceiling, targetEvaluation } = result;
  const capsById = useMemo(
    () =>
      ceiling.leverCaps.reduce((acc, lever) => {
        acc[lever.id] = lever.cap;
        return acc;
      }, {}),
    [ceiling.leverCaps],
  );

  const hasSimulation = Object.values(state.simulation || {}).some((value) => value > 0);

  return (
    <div className="dg-step dg-result">
      <div className="dg-result-head">
        <span className="dg-result-eyebrow">Результат диагностики</span>
        <h2 className="dg-step-title">
          {result.niche.label} · {formatMoneyShort(result.state.revenue)} в месяц
        </h2>
      </div>

      <TrafficLight evaluation={targetEvaluation} ceilingRevenue={ceiling.revenue} />

      <section className="dg-block">
        <h3 className="dg-block-title">Где вы сейчас</h3>
        <div className="dg-metrics">
          <div className="dg-metric">
            <span className="dg-metric-label">Выручка в месяц</span>
            <span className="dg-metric-value">{formatMoney(result.state.revenue)}</span>
          </div>
          <div className="dg-metric">
            <span className="dg-metric-label">Средний чек</span>
            <span className="dg-metric-value">{formatMoney(result.state.averageCheck)}</span>
          </div>
          <div className="dg-metric">
            <span className="dg-metric-label">Клиентов в месяц</span>
            <span className="dg-metric-value">{formatNumber(result.state.clients)}</span>
          </div>
          {result.state.hasConversion ? (
            <div className="dg-metric">
              <span className="dg-metric-label">Доходит до покупки</span>
              <span className="dg-metric-value">
                {formatPercent(result.state.conversionPercent, 1)}
              </span>
            </div>
          ) : null}
        </div>
      </section>

      <section className="dg-block dg-block--accent">
        <h3 className="dg-block-title">Ваш главный резерв</h3>
        {primaryLever ? (
          <>
            <p className="dg-primary-lever">{primaryLever.lever.plainLabel}</p>
            <p className="dg-primary-text">
              {primaryLever.lever.action}. В вашей нише это реально поднять примерно
              на {formatPercent(primaryLever.capPercent)} — это{' '}
              <strong>{formatMoney(primaryLever.gain)}</strong> дополнительной выручки в месяц.
            </p>
            <p className="dg-primary-timing">
              Срок: {primaryLever.lever.timing.label}
            </p>
          </>
        ) : null}
      </section>

      <section className="dg-block">
        <div className="dg-block-head">
          <h3 className="dg-block-title">Посчитайте сами</h3>
          {hasSimulation ? (
            <button type="button" className="dg-link-btn" onClick={resetSimulation}>
              Сбросить
            </button>
          ) : null}
        </div>
        <p className="dg-block-lead">
          Подвигайте ползунки и посмотрите, как меняется выручка. Пределы взяты по вашей нише.
        </p>

        <div className="dg-sliders">
          {ceiling.leverCaps.map((lever) => (
            <LeverSlider
              key={lever.id}
              leverId={lever.id}
              label={LEVER_MAP[lever.id].plainLabel}
              currentValue={result.state.levers[lever.id]}
              delta={state.simulation[lever.id] || 0}
              maxDelta={capsById[lever.id]}
              onChange={(value) => setSimulation(lever.id, value)}
            />
          ))}
        </div>

        <div className="dg-sim-result">
          <span className="dg-sim-label">Выручка при этих изменениях</span>
          <span className="dg-sim-value">{formatMoney(simulation.revenue)}</span>
          {simulation.gain > 0 ? (
            <span className="dg-sim-gain">
              {formatSignedPercent(simulation.gainPercent)} · +{formatMoney(simulation.gain)}
            </span>
          ) : null}
        </div>

        <p className="dg-note">{result.disclaimers.ceiling}</p>
      </section>

      <div className="dg-actions">
        <button type="button" className="dg-btn dg-btn--ghost" onClick={onDownload}>
          Скачать краткий результат
        </button>
      </div>

      <section className="dg-paywall">
        <span className="dg-paywall-eyebrow">Полный разбор</span>
        <h3 className="dg-paywall-title">
          Вы увидели <em>что</em>. Полный разбор отвечает на <em>почему</em> и <em>что делать</em>
        </h3>

        <ul className="dg-paywall-list">
          <li>Три сценария под вашу цель: минимальный, сбалансированный и полный</li>
          <li>Сколько процентов цели закрывает каждый и за какой срок</li>
          <li>Пошаговый план действий на выбранный сценарий</li>
          <li>Каналы продвижения, которые работают именно в вашей нише в Казахстане</li>
          <li>Разбор в готовом документе, который можно отдать команде</li>
        </ul>

        <div className="dg-paywall-cta">
          <button type="button" className="dg-btn dg-btn--primary" onClick={onUnlock}>
            Получить полный разбор
          </button>
          <span className="dg-paywall-price">25 000 ₸ · оплата через Kaspi скоро</span>
        </div>
      </section>
    </div>
  );
};

export default StepResult;
